// Components/Services/ADUAndCustomHomes/WhoItsFor.jsx
import React from "react";
import { motion } from "framer-motion";
import { FiHome, FiDollarSign, FiUsers, FiTrendingUp, FiTool, FiMapPin } from "react-icons/fi";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const aduAudience = [
  {
    icon: <FiDollarSign className="w-5 h-5 text-[#212529]" />,
    title: "Rental Income Seekers",
    desc: "Homeowners who want to generate passive income from their existing property without buying new real estate.",
  },
  {
    icon: <FiUsers className="w-5 h-5 text-[#212529]" />,
    title: "Multi-Generational Families",
    desc: "Families looking to house aging parents, adult children, or in-laws on the same property with privacy.",
  },
  {
    icon: <FiTrendingUp className="w-5 h-5 text-[#212529]" />,
    title: "Property Value Investors",
    desc: "Owners who want to increase their home's resale value with a permitted, income-generating unit.",
  },
];

const customHomeAudience = [
  {
    icon: <FiHome className="w-5 h-5 text-[#212529]" />,
    title: "Custom Home Buyers",
    desc: "Clients who own land or are purchasing a lot and want a home built exactly to their specifications — not a spec house.",
  },
  {
    icon: <FiMapPin className="w-5 h-5 text-[#212529]" />,
    title: "Land Owners",
    desc: "Those who have inherited or purchased vacant land in California and want to build the right way with a trusted contractor.",
  },
  {
    icon: <FiTool className="w-5 h-5 text-[#212529]" />,
    title: "High-Expectation Clients",
    desc: "Buyers who have been burned by volume builders and want direct access to the people building their home.",
  },
];

const WhoItsFor = () => {
  return (
    <section className="bg-[#f8f9fa] py-16 px-6">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.p variants={fadeUp} className="text-[#6c757d] uppercase tracking-widest text-xs font-bold mb-3">
            Is This Right For You?
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-black text-[#212529] leading-tight mb-4">
            Who These Services Are Built For
          </motion.h2>
          <motion.p variants={fadeUp} className="text-[#495057] text-base leading-relaxed max-w-2xl mx-auto">
            We work best with clients who value quality over speed, expect direct communication, and are ready to invest in something that lasts.
          </motion.p>
        </motion.div>

        {/* Two columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

          {/* ADU Column */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeUp} className="mb-6">
              <div className="inline-flex items-center gap-2 bg-[#212529] text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full mb-3">
                <FiHome className="w-4 h-4" /> ADUs
              </div>
              <h3 className="text-xl font-black text-[#212529]">Who Should Build an ADU</h3>
              <p className="text-sm text-[#6c757d] mt-1">If you own property in California, an ADU is one of the highest-ROI investments you can make right now.</p>
            </motion.div>

            <div className="space-y-4">
              {aduAudience.map((item, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  whileHover={{ x: 6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="flex items-start gap-4 bg-white rounded-xl px-5 py-4 border border-[#dee2e6] shadow-sm cursor-default"
                >
                  <div className="w-10 h-10 rounded-full bg-[#f8f9fa] border border-[#dee2e6] flex items-center justify-center flex-shrink-0 shadow-sm">
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-[#212529] font-bold text-sm">{item.title}</p>
                    <p className="text-[#6c757d] text-xs mt-1 leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Custom Homes Column */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeUp} className="mb-6">
              <div className="inline-flex items-center gap-2 bg-[#343a40] text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full mb-3">
                <FiTool className="w-4 h-4" /> Custom Homes
              </div>
              <h3 className="text-xl font-black text-[#212529]">Who Should Build a Custom Home</h3>
              <p className="text-sm text-[#6c757d] mt-1">Custom builds are for those who know exactly what they want — and won't settle for anything less.</p>
            </motion.div>

            <div className="space-y-4">
              {customHomeAudience.map((item, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  whileHover={{ x: 6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="flex items-start gap-4 bg-white rounded-xl px-5 py-4 border border-[#dee2e6] shadow-sm cursor-default"
                >
                  <div className="w-10 h-10 rounded-full bg-[#f8f9fa] border border-[#dee2e6] flex items-center justify-center flex-shrink-0 shadow-sm">
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-[#212529] font-bold text-sm">{item.title}</p>
                    <p className="text-[#6c757d] text-xs mt-1 leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

        </div>

        {/* Bottom CTA nudge */}
        <motion.div
          className="mt-12 text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <p className="text-[#495057] text-sm mb-4">Not sure which service fits your situation? We'll tell you in 15 minutes.</p>
          <a
            href="/contact"
            className="inline-block bg-[#212529] hover:bg-[#343a40] text-white text-sm font-semibold uppercase tracking-wider px-8 py-3 rounded-full transition duration-300"
          >
            Talk to Our Team →
          </a>
        </motion.div>

      </div>
    </section>
  );
};

export default WhoItsFor;